<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue'

const props = defineProps({
	siteKey: { type: String, required: true },
	theme: { type: String, default: 'dark' },
	size: { type: String, default: 'normal' },
})

const emit = defineEmits(['verified', 'expired', 'error'])

const container = ref(null)
let widgetId = null

const SCRIPT_SRC =
	'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

function loadScript() {
	if (window.__turnstileScriptPromise) return window.__turnstileScriptPromise
	window.__turnstileScriptPromise = new Promise((resolve, reject) => {
		if (window.turnstile) return resolve()
		const s = document.createElement('script')
		s.src = SCRIPT_SRC
		s.async = true
		s.defer = true
		s.onload = () => resolve()
		s.onerror = () => reject(new Error('Failed to load Turnstile'))
		document.head.appendChild(s)
	})
	return window.__turnstileScriptPromise
}

async function render() {
	try {
		await loadScript()
	} catch (err) {
		emit('error', err)
		return
	}
	if (!window.turnstile || !container.value) return
	widgetId = window.turnstile.render(container.value, {
		sitekey: props.siteKey,
		theme: props.theme,
		size: props.size,
		callback: (token) => emit('verified', token),
		'expired-callback': () => emit('expired'),
		'error-callback': () => emit('error'),
	})
}

function reset() {
	if (window.turnstile && widgetId !== null) {
		window.turnstile.reset(widgetId)
	}
}

defineExpose({ reset })

onMounted(render)

onBeforeUnmount(() => {
	if (window.turnstile && widgetId !== null) {
		try {
			window.turnstile.remove(widgetId)
		} catch (err) {
			/* widget already gone */
		}
	}
})
</script>

<template>
	<div ref="container" class="cf-turnstile-widget"></div>
</template>
